import type React from "react";
import { inputStyle, btnSecondary, type as text } from "../../theme";
import { ControlTagFilters, type TagDimension, type TagFilter } from "../ControlTags";

// The one filter bar above a standard's list: search, status, group (clause / theme / objective /
// law), the tag filters, expand / collapse all and the count shown. Pages keep their own
// page-level choices (the SOC 2 report type, the PCI SAQ) outside it.

export interface FilterOption { value: string; label: string }

/** The status filter choices, in the wording the list itself uses. */
export function statusFilterOptions(novice: boolean): FilterOption[] {
  return novice
    ? [
        { value: "all", label: "Any status" },
        { value: "NOT_STARTED", label: "Not started yet" },
        { value: "IN_PROGRESS", label: "Working on it" },
        { value: "GAP", label: "Need to fix this" },
        { value: "COMPLIANT", label: "We've done this" },
      ]
    : [
        { value: "all", label: "Any status" },
        { value: "NOT_STARTED", label: "Not assessed" },
        { value: "IN_PROGRESS", label: "In progress" },
        { value: "GAP", label: "Gap identified" },
        { value: "COMPLIANT", label: "Compliant" },
      ];
}

const select: React.CSSProperties = { ...inputStyle, width: "auto", maxWidth: 260, padding: "7px 10px" };
const small: React.CSSProperties = { ...btnSecondary, padding: "6px 10px", fontSize: text.meta.fontSize };

export function ControlFilterBar({
  search, onSearch, searchPlaceholder = "Search by reference or title",
  status, onStatus, statusOptions,
  group, onGroup, groupOptions, groupLabel = "All groups",
  tagFilter, onTagFilter, tagDimensions, accent,
  count, onExpandAll, onCollapseAll, children,
}: {
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder?: string;
  status?: string;
  onStatus?: (v: string) => void;
  statusOptions?: FilterOption[];
  group?: string;
  onGroup?: (v: string) => void;
  groupOptions?: FilterOption[];
  /** The "all" choice of the group filter ("All clauses", "All objectives"). */
  groupLabel?: string;
  tagFilter?: TagFilter;
  onTagFilter?: (f: TagFilter) => void;
  tagDimensions?: readonly TagDimension[];
  accent?: string;
  count?: { shown: number; total: number; noun: string };
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        style={{ ...inputStyle, width: 240, maxWidth: "100%", padding: "7px 10px" }}
      />
      {statusOptions && onStatus && (
        <select aria-label="Status" value={status} onChange={(e) => onStatus(e.target.value)} style={select}>
          {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {groupOptions && groupOptions.length > 1 && onGroup && (
        <select aria-label={groupLabel} value={group} onChange={(e) => onGroup(e.target.value)} style={select}>
          <option value="all">{groupLabel}</option>
          {groupOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {tagFilter && onTagFilter && <ControlTagFilters value={tagFilter} onChange={onTagFilter} accentColor={accent} dimensions={tagDimensions} />}
      {children}
      <span style={{ flex: 1 }} />
      {count && (
        <span style={text.hint}>
          {count.shown === count.total ? `${count.total} ${count.noun}` : `${count.shown} of ${count.total} ${count.noun}`}
        </span>
      )}
      {onExpandAll && <button type="button" onClick={onExpandAll} style={small}>Expand all</button>}
      {onCollapseAll && <button type="button" onClick={onCollapseAll} style={small}>Collapse all</button>}
    </div>
  );
}

/** Whether a reference or title matches the search box. */
export function matchesControlSearch(search: string, ...fields: Array<string | null | undefined>): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => !!f && f.toLowerCase().includes(q));
}

/** Tracks which groups are open; "all" until the person closes one. */
export type GroupExpansion = Set<string> | "all" | "none";

export function isGroupOpen(state: GroupExpansion, key: string): boolean {
  if (state === "all") return true;
  if (state === "none") return false;
  return state.has(key);
}

export function toggleGroup(state: GroupExpansion, key: string, allKeys: string[]): GroupExpansion {
  const next = new Set(state === "all" ? allKeys : state === "none" ? [] : state);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
}
